import { Box, Button, CircularProgress } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Await, defer, Link, useLoaderData } from "react-router-dom";
import { getCustomerSystemMails } from "../../api/systemMailClient";
import { listCustomers } from "../../api/customerClient";
import SystemMailTemplateTable from "../../components/SystemMail/SystemMailTemplateTable";
import { AdminSystemMailTemplateOverview } from "../../components/SystemMail/AdminSystemMailTemplateOverview";
import { SystemMailWithoutBody } from "../../model/SystemMail";
import { Customer } from "../../model/Customer";
import classes from "../../styling/pages/Manager.module.css";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../App";
import { OrganizationLevel } from "../../model/SelfUser";
import { Suspense } from "react";

const SystemMailTemplateManagerPage: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useContext(UserContext);
    const isAdmin = user?.organizationLevel === OrganizationLevel.SYSTEM;
    const { templates } = useLoaderData() as {
        templates: Promise<SystemMailWithoutBody[]>;
    };
    const [customers, setCustomers] = useState<Customer[]>([]);

    useEffect(() => {
        if (!isAdmin) return;
        let isMounted = true;
        listCustomers("1")
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => {
                if (isMounted) setCustomers(data);
            })
            .catch(() => {
                if (isMounted) setCustomers([]);
            });
        return () => {
            isMounted = false;
        };
    }, [isAdmin]);

    if (isAdmin) {
        return (
            <Suspense fallback={<CircularProgress />}>
                <AdminSystemMailTemplateOverview
                    templates={templates}
                    customers={customers}
                />
            </Suspense>
        );
    }

    return (
        <>
            <Box className={classes.buttonContainer}>
                <Link to="new">
                    <Button variant="contained">
                        {t("systemMailTemplate.manager.newTemplate")}
                    </Button>
                </Link>
            </Box>
            <Suspense fallback={<CircularProgress />}>
                <Await resolve={templates}>
                    {(loadedTemplates) => (
                        <SystemMailTemplateTable templates={loadedTemplates} />
                    )}
                </Await>
            </Suspense>
        </>
    );
};

export default SystemMailTemplateManagerPage;

async function loadCustomerSystemMailTemplates(customerId: string) {
    return getCustomerSystemMails(customerId);
}

export async function loader({
    params,
}: {
    request: Request;
    params: { customerId: string };
}) {
    const customerId = params.customerId;
    return defer({
        templates: loadCustomerSystemMailTemplates(customerId),
    });
}
